/**
 * "Proovi vestlust", step one: the company the AI talks to.
 *
 * The AI's context is a real company's (its name, website and the brain's
 * findings), so the rehearsal starts by naming one: a company from this
 * campaign's queue, or any register code or domain typed by hand. An alert
 * campaign (ring R9) needs a company whose website has a defect found right now,
 * and the step says so.
 */
import { useEffect, useState } from "react";
import { Loader2, Play } from "lucide-react";
import { Button, Input } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import { listAiCampaignTargets, type AiCallTarget } from "../../../../../lib/aiCampaigns";
import { manualCompany } from "../../../../../lib/aiRehearse";
import type { AiTestCallTarget } from "../../../../../lib/aiTestCallPreview";
import { SelectInput } from "../../../../ui";

export function RehearsePicker({
  campaignId,
  alert = false,
  onPick,
}: {
  campaignId: string;
  /** The saved campaign is an alert campaign, so the server rehearses an alert opening. */
  alert?: boolean;
  onPick: (target: AiTestCallTarget) => void;
}) {
  const t = useT();
  const [queue, setQueue] = useState<AiCallTarget[] | null>(null);
  const [chosen, setChosen] = useState("");
  const [typed, setTyped] = useState("");

  useEffect(() => {
    let alive = true;
    listAiCampaignTargets(campaignId).then(
      (page) => {
        if (!alive) return;
        // A target the AI could be pointed at needs a company or a website to name.
        const usable = page.rows.filter((row) => row.regCode || row.domain);
        setQueue(usable);
        setChosen(usable[0]?.id ?? "");
      },
      () => {
        if (alive) setQueue([]);
      },
    );
    return () => {
      alive = false;
    };
  }, [campaignId]);

  const manual = manualCompany(typed);
  const fromQueue = queue?.find((row) => row.id === chosen) ?? null;

  function start() {
    if (manual) {
      onPick({ ...manual, campaignId });
      return;
    }
    if (fromQueue) onPick({ regCode: fromQueue.regCode, domain: fromQueue.domain, name: fromQueue.companyName, campaignId });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h4 className="text-sm font-semibold">{t("aiCalls.rehearse.pick.title")}</h4>
        <p className="crm-sub text-sm">{t("aiCalls.rehearse.pick.body")}</p>
        {alert ? <p className="crm-sub text-sm">{t("aiCalls.rehearse.pick.alertHint")}</p> : null}
      </div>

      {queue === null ? (
        <div className="flex justify-center py-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : queue.length > 0 ? (
        <div className="space-y-1.5">
          <p className="crm-lab !mt-0">{t("aiCalls.rehearse.pick.queue")}</p>
          <SelectInput value={chosen} onChange={setChosen} label={t("aiCalls.rehearse.pick.queue")} disabled={Boolean(manual)}>
            {queue.map((row) => (
              <option key={row.id} value={row.id}>
                {row.companyName || row.domain || row.regCode}
              </option>
            ))}
          </SelectInput>
        </div>
      ) : (
        <p className="crm-sub text-sm">{t("aiCalls.rehearse.pick.queueNone")}</p>
      )}

      <div className="space-y-1.5">
        <label className="crm-lab !mt-0 block" htmlFor="rehearse-company">
          {t("aiCalls.rehearse.pick.manual")}
        </label>
        <Input
          id="rehearse-company"
          value={typed}
          placeholder={t("aiCalls.rehearse.pick.manualPlaceholder")}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && manual) start();
          }}
        />
        {typed.trim() && !manual ? <p className="text-xs text-destructive">{t("aiCalls.rehearse.pick.manualInvalid")}</p> : null}
      </div>

      <div className="flex justify-end">
        <Button onClick={start} disabled={!manual && !fromQueue}>
          <Play className="size-4" />
          {t("aiCalls.rehearse.pick.start")}
        </Button>
      </div>
    </div>
  );
}
